// src/components/EditUnderwriterModal.tsx
import { useState, useEffect } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

interface UnderwriterData {
  id: number;
  status: 'Active' | 'Inactive';
  underwriter_name: string;
  consultant_reference_number: string;
  tax_number: string;
  consultant_name: string;
  consultant_surname: string;
  consultant_phone_number: string;
  consultant_email: string;
  quotations_email: string;
  claims_email: string;
  instructions_email: string;
}

interface EditUnderwriterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  underwriterId: number;
}

const EditUnderwriterModal = ({ isOpen, onClose, onSave, underwriterId }: EditUnderwriterModalProps) => {
  const [formData, setFormData] = useState<UnderwriterData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && underwriterId) {
      fetchUnderwriter();
    }
  }, [isOpen, underwriterId]);

  const fetchUnderwriter = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/underwriters/${underwriterId}`);
      setFormData(res.data.data || res.data);
    } catch (err) {
      console.error("Error fetching underwriter:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    if (!formData) return;
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData) return;

    setSaving(true);
    try {
      const payload = {
        underwriter_name: formData.underwriter_name,
        consultant_reference_number: formData.consultant_reference_number,
        tax_number: formData.tax_number,
        consultant_name: formData.consultant_name,
        consultant_surname: formData.consultant_surname,
        consultant_email: formData.consultant_email,
        consultant_phone_number: formData.consultant_phone_number,
        quotations_email: formData.quotations_email,
        claims_email: formData.claims_email,
        instructions_email: formData.instructions_email,
        status: formData.status,
      };

      await axios.put(`${API_URL}/api/underwriters/${underwriterId}`, payload);
      onSave();
      onClose();
    } catch (err) {
      console.error("Error updating underwriter:", err);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-xl shadow-lg w-full max-w-3xl">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!formData) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-xl shadow-lg w-full max-w-3xl">
          <p>Underwriter not found</p>
          <button
            onClick={onClose}
            className="mt-4 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-lg"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white p-6 rounded-xl shadow-lg w-full max-w-3xl max-h-screen overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Edit Underwriter</h1>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Underwriter Name</label>
            <input
              type="text"
              name="underwriter_name"
              value={formData.underwriter_name}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Consultant Reference Number</label>
              <input
                type="text"
                name="consultant_reference_number"
                value={formData.consultant_reference_number}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tax Number</label>
              <input
                type="text"
                name="tax_number"
                value={formData.tax_number || ""}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Consultant Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Consultant First Name</label>
              <input
                type="text"
                name="consultant_name"
                value={formData.consultant_name}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Consultant Last Name</label>
              <input
                type="text"
                name="consultant_surname"
                value={formData.consultant_surname}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          {/* Contact Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Consultant Email</label>
              <input
                type="email"
                name="consultant_email"
                value={formData.consultant_email}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Consultant Phone Number</label>
              <input
                type="text"
                name="consultant_phone_number"
                value={formData.consultant_phone_number}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          {/* Other Emails */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Quotations Email</label>
              <input
                type="email"
                name="quotations_email"
                value={formData.quotations_email || ""}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Claims Email</label>
              <input
                type="email"
                name="claims_email"
                value={formData.claims_email || ""}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Instructions Email</label>
              <input
                type="email"
                name="instructions_email"
                value={formData.instructions_email || ""}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">Status *</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>

          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-6 py-2 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUnderwriterModal;